import { useEffect, useRef, useState } from 'react';
import type { FaceState } from '@/state/types';
import { FACE_GLYPHS, GLITCH_CHARS } from './glyphs';

export interface GlitchResult {
  eyes: string;
  mouth: string;
  isGlitching: boolean;
}

export const GLITCH_DURATION_MS = 100;
export const GLITCH_FRAME_MS = 25;

function randomGlitchLine(template: string): string {
  let out = '';
  for (const ch of template) {
    if (ch === ' ') {
      out += ' ';
      continue;
    }
    const idx = Math.floor(Math.random() * GLITCH_CHARS.length);
    out += GLITCH_CHARS[idx] ?? '$';
  }
  return out;
}

/**
 * When `target` changes, render scrambled `$%#@` glyphs for
 * GLITCH_DURATION_MS then settle on the target face. No glitch on first mount.
 * Reduced motion skips the scramble entirely.
 */
export function useGlitchSwap(target: FaceState, reducedMotion = false): GlitchResult {
  const [rendered, setRendered] = useState<GlitchResult>(() => ({
    ...FACE_GLYPHS[target],
    isGlitching: false,
  }));
  const isFirstRun = useRef(true);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }

    const targetGlyphs = FACE_GLYPHS[target];

    if (reducedMotion) {
      setRendered({ ...targetGlyphs, isGlitching: false });
      return;
    }

    const frameTimer = window.setInterval(() => {
      setRendered({
        eyes: randomGlitchLine(targetGlyphs.eyes),
        mouth: randomGlitchLine(targetGlyphs.mouth),
        isGlitching: true,
      });
    }, GLITCH_FRAME_MS);

    const settleTimer = window.setTimeout(() => {
      window.clearInterval(frameTimer);
      setRendered({ ...targetGlyphs, isGlitching: false });
    }, GLITCH_DURATION_MS);

    return () => {
      window.clearInterval(frameTimer);
      window.clearTimeout(settleTimer);
    };
  }, [target, reducedMotion]);

  return rendered;
}
